import React, { useEffect, useMemo, useState } from 'react';
import { listEvents, getEventNames, Event, ListResult } from '../../data/eventsService';
import EventDetailModal from './EventDetailModal';

type SortField = 'event_name' | 'timestamp' | 'user_name';
type SortDirection = 'asc' | 'desc';

export default function EventsListPage() {
  // UI state
  const [q, setQ] = useState('');
  const [eventNameFilter, setEventNameFilter] = useState<string>('all');
  const [eventNames, setEventNames] = useState<string[]>([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Data state
  const [res, setRes] = useState<ListResult | null>(null);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const totalPages = useMemo(() => {
    if (!res) return 1;
    return Math.max(1, Math.ceil(res.total / pageSize));
  }, [res, pageSize]);

  const sortedItems = useMemo(() => {
    if (!res?.items) return [];
    const items = [...res.items];
    items.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'event_name':
          aVal = a.event_name.toLowerCase();
          bVal = b.event_name.toLowerCase();
          break;
        case 'timestamp':
          aVal = new Date(a.timestamp).getTime();
          bVal = new Date(b.timestamp).getTime();
          break;
        case 'user_name':
          aVal = (a.user_name || '').toLowerCase();
          bVal = (b.user_name || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return items;
  }, [res?.items, sortField, sortDirection]);

  // Load event names on mount
  useEffect(() => {
    getEventNames().then(setEventNames);
  }, []);

  // Load events when filters change
  useEffect(() => {
    setLoading(true);
    listEvents({
      q,
      event_name: eventNameFilter !== 'all' ? eventNameFilter : undefined,
      page,
      pageSize,
    })
      .then(setRes)
      .finally(() => setLoading(false));
  }, [q, eventNameFilter, page, pageSize]);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 opacity-30"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    if (sortDirection === 'asc') {
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-full bg-[#FFFCEF] text-[#2C2731] p-6 space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-3">
        <div>
          <h1 className="text-3xl font-medium text-[#2C2731] font-recoleta">
            Events Analytics
          </h1>
          <p className="text-sm text-[#2C2731]/80 md:text-base">
            Monitor and analyze user events across the platform
          </p>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-[#214D3C] bg-[#214D3C] text-[#FEF6D6] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-[#FEF6D6]/70 font-medium">Total Events</div>
          </div>
          <div className="text-3xl font-medium font-recoleta text-[#FEF6D6]">
            {res?.total.toLocaleString() ?? 0}
          </div>
        </div>
        <div className="rounded-xl border border-[#214D3C] bg-[#214D3C] text-[#FEF6D6] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-[#FEF6D6]/70 font-medium">Event Types</div>
          </div>
          <div className="text-3xl font-medium font-recoleta text-[#FEF6D6]">
            {eventNames.length}
          </div>
        </div>
        <div className="rounded-xl border border-[#214D3C] bg-[#214D3C] text-[#FEF6D6] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-sm text-[#FEF6D6]/70 font-medium">Current Page</div>
          </div>
          <div className="text-3xl font-medium font-recoleta text-[#FEF6D6]">
            {page} / {totalPages}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-4 rounded-xl border border-[#2C2731] bg-[#2C2731] p-4 text-[#FEF6D6] shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="text-lg font-medium font-recoleta">All Events</div>
        </div>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <select
            value={eventNameFilter}
            onChange={(e) => {
              setPage(1);
              setEventNameFilter(e.target.value);
            }}
            className="rounded-lg border border-[#FEF6D6] bg-[#2C2731] px-3 py-2.5 text-sm text-[#FEF6D6] focus:border-[#F2EAC3] focus:outline-none focus:ring-2 focus:ring-[#F2EAC3]"
          >
            <option value="all">All Event Types</option>
            {eventNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <input
            placeholder="Search events, users…"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
            className="w-full rounded-lg border border-[#FEF6D6] bg-[#FFFCEF] px-3 py-2.5 text-sm text-[#2C2731] placeholder-[#2C2731]/50 focus:border-[#F2EAC3] focus:outline-none focus:ring-2 focus:ring-[#F2EAC3] sm:w-64"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#2C2731] text-[#FEF6D6]">
              <tr>
                <th
                  className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                  onClick={() => handleSort('event_name')}
                >
                  <div className="flex items-center gap-2">
                    Event Name
                    <SortIcon field="event_name" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                  onClick={() => handleSort('user_name')}
                >
                  <div className="flex items-center gap-2">
                    User
                    <SortIcon field="user_name" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                  onClick={() => handleSort('timestamp')}
                >
                  <div className="flex items-center gap-2">
                    Timestamp
                    <SortIcon field="timestamp" />
                  </div>
                </th>
                <th className="px-4 py-3 font-medium uppercase tracking-wide">Metadata</th>
                <th className="px-4 py-3 text-right font-medium uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2C2731]/20">
              {loading && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-sm text-[#2C2731]/70"
                  >
                    Loading events…
                  </td>
                </tr>
              )}

              {!loading && sortedItems.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-sm text-[#2C2731]/70"
                  >
                    No events found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedItems.map((event) => (
                  <tr
                    key={event.id}
                    className="bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F2EAC3] transition-colors cursor-pointer"
                    onClick={() => setSelectedEvent(event)}
                  >
                    <td className="px-4 py-4 align-top">
                      <div className="font-medium text-[#2C2731]">{event.event_name}</div>
                      <div className="text-xs text-[#2C2731]/50 font-mono mt-1">
                        {event.id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm">{event.user_name || 'Unknown'}</div>
                      <div className="text-xs text-[#2C2731]/70">{event.user_email}</div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm">{formatTimestamp(event.timestamp)}</div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-xs text-[#2C2731]/70">
                        {Object.keys(event.metadata).length} fields
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right align-top">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEvent(event);
                        }}
                        className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-3 py-2 text-xs font-semibold text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col gap-3 text-sm text-[#2C2731]/80 md:flex-row md:items-center md:justify-between">
        <div>
          Showing {sortedItems.length} of {res?.total ?? 0} events
        </div>
        <div className="flex items-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
              page <= 1
                ? 'cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40'
                : 'bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]'
            }`}
          >
            Previous
          </button>
          <div className="px-2 text-sm text-[#2C2731]">
            Page {page} / {totalPages}
          </div>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
              page >= totalPages
                ? 'cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40'
                : 'bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Detail modal */}
      {selectedEvent && (
        <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}
